import { ToastProps } from "./Toast";
import { useToasts } from "./useToasts";
import { ANIMATION_DURATION } from "./constants";
import { ToastProviderContextProps } from "./ToastProvider";

/**
 * Updates toasts after close
 * @param setToasts - Function to update toasts
 * @param setToastHeights - Function to update toast heights
 * @param id - Optional toast ID to close specific toast, or undefined to close all
 */
const setToastsAfterClose = (
  setToasts: ToastProviderContextProps["setToasts"],
  setToastHeights: ToastProviderContextProps["setToastHeights"],
  id?: string,
) => {
  setToasts((prev) => (id ? prev.filter((t) => t.id !== id) : []));
  setToastHeights((prev) => (id ? prev.filter((t) => t.id !== id) : []));
};

/**
 * Closes a toast with animation support
 * @param setToasts - Function to update toasts
 * @param setToastHeights - Function to update toast heights
 * @param closingToastClassName - CSS class for closing animation
 * @param id - Optional toast ID to close specific toast, or undefined to close all
 */
const closeToast = (
  setToasts: ToastProviderContextProps["setToasts"],
  setToastHeights: ToastProviderContextProps["setToastHeights"],
  closingToastClassName: ToastProps["toastClassName"],
  id?: string,
) => {
  const prefersReducedMotion = globalThis.matchMedia(
    `(prefers-reduced-motion: reduce)`,
  ).matches;

  if (prefersReducedMotion) {
    setToastsAfterClose(setToasts, setToastHeights, id);
  } else {
    // show the close animation
    setToasts((prev) =>
      prev.map((t) => {
        if (!id || t.id === id) {
          return {
            ...t,
            toastClassName: closingToastClassName,
          };
        }
        return t;
      }),
    );
    // actually remove the toast
    setTimeout(() => {
      setToastsAfterClose(setToasts, setToastHeights, id);
    }, ANIMATION_DURATION - 10);
  }
};

/**
 * Hook that returns a function to close toasts
 * @param closingToastClassName - CSS class for closing animation
 * @returns Function to close toasts by ID or all toasts
 */
export const useCloseToast = (
  closingToastClassName: ToastProps["toastClassName"],
) => {
  const { setToasts, setToastHeights } = useToasts();
  return (id?: string) => {
    closeToast(setToasts, setToastHeights, closingToastClassName, id);
  };
};
